/**
 * pRI Route Utilities — Frontend-side route → module resolution.
 * Epic E-PRI-2, Stories S-PRI-2.5 + S-PRI-2.6
 *
 * Lightweight browser-side version of route-module-map.ts.
 * Used by PriWelcome and usePriChat for context-aware quick actions.
 */

export interface RouteModuleEntry {
  module: string;
  pageName: string;
}

/**
 * Map of known frontend routes to pRI modules.
 * Kept in sync with the backend route-module-map.ts.
 */
const ROUTE_MODULE_MAP: Record<string, RouteModuleEntry> = {
  '/': { module: 'DASHBOARD', pageName: 'Kezdolap' },
  '/dashboard': { module: 'DASHBOARD', pageName: 'Kezdolap' },
  '/rentals': { module: 'RENTAL', pageName: 'Berlesek lista' },
  '/rentals/new': { module: 'RENTAL', pageName: 'Uj berles inditas' },
  '/rentals/:id': { module: 'RENTAL', pageName: 'Berles reszletei' },
  '/rentals/:id/extend': { module: 'RENTAL', pageName: 'Berles hosszabbitas' },
  '/rentals/:id/return': { module: 'RENTAL', pageName: 'Berles visszavetel' },
  '/rental-equipment': { module: 'RENTAL', pageName: 'Bergepek' },
  '/rental-equipment/:id': { module: 'RENTAL', pageName: 'Bergep reszletei' },
  '/service/worksheets': { module: 'SERVICE', pageName: 'Szerviz munkalapok' },
  '/service/worksheets/new': { module: 'SERVICE', pageName: 'Uj munkalap' },
  '/service/worksheets/:id': { module: 'SERVICE', pageName: 'Munkalap reszletei' },
  '/service/warranty': { module: 'SERVICE', pageName: 'Garancia kezeles' },
  '/service/warranty/:id': { module: 'SERVICE', pageName: 'Garancia reszletei' },
  '/service/parts': { module: 'SERVICE', pageName: 'Alkatresz katalogus' },
  '/service/norma': { module: 'SERVICE', pageName: 'Szerviz normak' },
  '/sales/pos': { module: 'SALES', pageName: 'Penztar (POS)' },
  '/sales/invoices': { module: 'SALES', pageName: 'Szamlak' },
  '/sales/invoices/new': { module: 'SALES', pageName: 'Uj szamla' },
  '/sales/invoices/:id': { module: 'SALES', pageName: 'Szamla reszletei' },
  '/sales/quotes': { module: 'SALES', pageName: 'Arajanlatok' },
  '/sales/quotes/new': { module: 'SALES', pageName: 'Uj arajanlat' },
  '/sales/quotes/:id': { module: 'SALES', pageName: 'Arajanlat reszletei' },
  '/sales/credit-notes': { module: 'SALES', pageName: 'Helyesbito szamlak' },
  '/cash': { module: 'CASH', pageName: 'Penztar kezeles' },
  '/cash/daily': { module: 'CASH', pageName: 'Napi zaras' },
  '/cash/register': { module: 'CASH', pageName: 'Kassza muveletek' },
  '/partners': { module: 'PARTNER', pageName: 'Partnerek' },
  '/partners/new': { module: 'PARTNER', pageName: 'Uj partner' },
  '/partners/:id': { module: 'PARTNER', pageName: 'Partner reszletei' },
  '/partners/:id/edit': { module: 'PARTNER', pageName: 'Partner szerkesztes' },
  '/inventory': { module: 'INVENTORY', pageName: 'Keszlet' },
  '/inventory/stock-count': { module: 'INVENTORY', pageName: 'Leltar' },
  '/inventory/transfers': { module: 'INVENTORY', pageName: 'Raktarkozi mozgas' },
  '/inventory/goods-receipt': { module: 'INVENTORY', pageName: 'Bevetelezes' },
  '/goods-receipt': { module: 'INVENTORY', pageName: 'Arubeszeres' },
  '/admin': { module: 'ADMIN', pageName: 'Adminisztracio' },
  '/admin/users': { module: 'ADMIN', pageName: 'Felhasznalok' },
  '/admin/settings': { module: 'ADMIN', pageName: 'Rendszerbeallitasok' },
  '/admin/audit-log': { module: 'ADMIN', pageName: 'Audit naplo' },
  '/vehicles': { module: 'ADMIN', pageName: 'Jarmuvek' },
  '/reports': { module: 'DASHBOARD', pageName: 'Riportok' },
  '/tasks': { module: 'DASHBOARD', pageName: 'Feladatok' },
};

/**
 * Resolve a frontend pathname to a pRI module + page name.
 */
export function resolveRouteModule(pathname: string): RouteModuleEntry | null {
  const normalized = pathname === '/' ? '/' : pathname.replace(/\/+$/, '');

  // 1. Exact match
  const exact = ROUTE_MODULE_MAP[normalized];
  if (exact) return exact;

  // 2. Replace UUID/numeric segments with :id
  const parameterized = normalized
    .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '/:id')
    .replace(/\/\d+/g, '/:id');

  const paramMatch = ROUTE_MODULE_MAP[parameterized];
  if (paramMatch) return paramMatch;

  // 3. Handle alphanumeric IDs (e.g., /rentals/abc-123)
  const genericParam = parameterized.replace(/\/([^/]*\d[^/]*)/g, '/:id');
  const genericMatch = ROUTE_MODULE_MAP[genericParam];
  if (genericMatch) return genericMatch;

  // 4. Progressive path shortening
  const segments = normalized.split('/').filter(Boolean);
  for (let i = segments.length - 1; i >= 1; i--) {
    const shortened = '/' + segments.slice(0, i).join('/');
    const shortenedMatch = ROUTE_MODULE_MAP[shortened];
    if (shortenedMatch) return shortenedMatch;
  }

  return null;
}

/**
 * Get a context-specific "How to" button label for the current page.
 */
export function getContextualHowToLabel(pageName: string): string {
  const labelMap: Record<string, string> = {
    'Berlesek lista': 'Hogyan inditsak berlest?',
    'Uj berles inditas': 'Hogyan toltsem ki?',
    'Berles reszletei': 'Mit tehetek itt?',
    'Berles hosszabbitas': 'Hogyan hosszabbitsak?',
    'Berles visszavetel': 'Hogyan vegyek vissza?',
    Bergepek: 'Hogyan kezelem a bergepeket?',
    'Szerviz munkalapok': 'Hogyan nyissak munkalapot?',
    'Uj munkalap': 'Hogyan toltsem ki?',
    'Munkalap reszletei': 'Mit tehetek itt?',
    'Garancia kezeles': 'Hogyan kezelem a garanciat?',
    'Alkatresz katalogus': 'Hogyan keressek alkatreszt?',
    'Penztar (POS)': 'Hogyan fizettessek?',
    Szamlak: 'Hogyan allitsak ki szamlat?',
    Arajanlatok: 'Hogyan keszitsek arajanlat?',
    'Penztar kezeles': 'Hogyan nyissak kasszat?',
    'Napi zaras': 'Hogyan zarjam a napot?',
    Partnerek: 'Hogyan vegyek fel partnert?',
    'Uj partner': 'Hogyan toltsem ki?',
    'Partner reszletei': 'Mit tehetek itt?',
    Keszlet: 'Hogyan ellenorizzem a keszletet?',
    Leltar: 'Hogyan vegezzek leltart?',
    Bevetelezes: 'Hogyan vetelezzek be?',
    Kezdolap: 'Hogyan hasznaljam a kezdolapot?',
    Felhasznalok: 'Hogyan kezelem a felhasznalokat?',
    Adminisztracio: 'Hogyan allitsam be?',
    Riportok: 'Hogyan keszitsek riportot?',
    Feladatok: 'Hogyan kezelem a feladatokat?',
  };

  return labelMap[pageName] ?? 'Hogyan csinaljam?';
}

/**
 * Get contextual "What is this page?" label.
 */
export function getContextualWhatIsLabel(pageName: string): string {
  if (!pageName) return 'Mi ez az oldal?';
  return `Mi ez: ${pageName}?`;
}
